/**
 * The omniscient view — the whole truth in PlayerView shape, for replays ("the fog lifts") and
 * observers. NEVER send it to a player still fighting: it is the opposite of the fog-of-war
 * boundary in build.ts. Who may watch a running war is decided by `mayObserve` below.
 *
 * Shape conventions (so renderer and HUD need no second code path):
 *  - `faction` = SPECTATOR (= NO_FACTION, 255): no unit is "own", nothing can be ordered.
 *  - `me` is a neutral placeholder FactionState (id SPECTATOR, team -1, no economy, no techs).
 *  - `units` holds every unit of every faction (full detail); `contacts` is empty.
 *  - `visible` is 1 everywhere; `owner` is the true owner; `supply` is each hex's owner's supply.
 *  - all battles with all participants, all couriers and telephone/radio dispatches, every
 *    faction's unexpired intercepts, and the events unfiltered (audience lists intact).
 */
import {
  type FactionId,
  type FactionState,
  type GameEvent,
  type GameState,
  type InterceptedOrder,
  NO_FACTION,
} from '../core/types';
import { copyUnit, factionPublic, weatherView } from './build';
import type { FactionPublic, PlayerView } from './types';

/** `PlayerView.faction` of an omniscient view. */
export const SPECTATOR = NO_FACTION;

export const isSpectatorView = (view: Pick<PlayerView, 'faction'>): boolean => view.faction === SPECTATOR;

/** Every nation fights for itself: no team has two members. */
export function isFreeForAll(factions: readonly Pick<FactionPublic, 'team'>[]): boolean {
  return new Set(factions.map((f) => f.team)).size === factions.length;
}

/**
 * The observer rule — who may watch a running war from outside the fog (the whole truth, or any
 * staff's view): the player of a nation that has capitulated, and only in a free-for-all.
 *
 * In a team game a fallen ally who saw everything could tell the allies still fighting where
 * every enemy division stands (chat, a voice call) — the fog would leak. There the fallen keep
 * their own staff map instead: vision is pooled by team, so it shows exactly what their living
 * allies already see, and a capitulated nation learns nothing new (it cannot decrypt or capture
 * anything). Capitulation is final and teams never change, so a permission once granted holds
 * for the rest of the war.
 */
export function mayObserve(
  factions: readonly Pick<FactionPublic, 'id' | 'team' | 'alive'>[],
  faction: FactionId,
): boolean {
  const f = factions.find((x) => x.id === faction);
  return f !== undefined && !f.alive && isFreeForAll(factions);
}

function spectatorMe(): FactionState {
  return {
    id: SPECTATOR,
    name: 'Omniscient',
    adjective: 'Neutral',
    color: 0x2b2620,
    team: -1,
    controller: 'human',
    aiDifficulty: 'veteran',
    capitalCity: -1,
    alive: true,
    capitulatedAt: -1,
    manpower: 0,
    industryRate: 0,
    production: [],
    research: { current: null, progress: 0, unlocked: [] },
    researchRate: 0,
    vp: 0,
    stats: {
      unitsProduced: 0,
      unitsLost: 0,
      enemyUnitsDestroyed: 0,
      citiesCaptured: 0,
      ordersIntercepted: 0,
      couriersLost: 0,
      peakHexes: 0,
    },
  };
}

/** Omniscient view of `state`; `events` are the raw events to include (usually this tick's). */
export function buildSpectatorView(state: GameState, events: readonly GameEvent[]): PlayerView {
  const n = state.owner.length;
  const supply = new Uint8Array(n);
  for (let h = 0; h < n; h++) {
    const o = state.owner[h];
    const s = o === NO_FACTION ? 0 : (state.supply[o]?.[h] ?? 0);
    if (s > 0) supply[h] = Math.round(s * 255);
  }
  const intercepts: InterceptedOrder[] = [];
  for (const list of state.intercepts)
    for (const i of list) if (i.expiresAt >= state.tick) intercepts.push({ ...i, order: { ...i.order } });
  return {
    tick: state.tick,
    faction: SPECTATOR,
    factions: state.factions.map(factionPublic),
    me: spectatorMe(),
    units: [...state.units.values()].map(copyUnit),
    contacts: [],
    couriers: [...state.couriers.values()].map((c) => ({
      ...c,
      order: { ...c.order },
      path: c.path.slice(),
    })),
    dispatches: state.dispatches.map((d) => ({ order: { ...d.order }, deliverAt: d.deliverAt })),
    battles: [...state.battles.values()].map((b) => ({
      id: b.id,
      hex: b.hex,
      attackers: b.attackers.slice(),
      defenders: b.defenders.slice(),
      startedAt: b.startedAt,
      balance: b.balance,
      attackDirs: b.attackDirs,
      attackerHexes: b.attackers.map((id) => state.units.get(id)?.hex ?? -1),
    })),
    visible: new Uint8Array(n).fill(1),
    owner: state.owner.slice(),
    supply,
    intercepts,
    events: events.map((e) => ({ ...e, audience: e.audience === 'all' ? 'all' : e.audience.slice() })),
    victoryClock: state.victoryClock ? { ...state.victoryClock } : null,
    winners: state.winners ? state.winners.slice() : null,
    weather: weatherView(state.weather),
  };
}
